import { ref, nextTick } from 'vue';

export function useKanbanDragDrop() {
  // State
  const draggedItem = ref(null);
  const dragOverColumn = ref(null);
  const isDragging = ref(false);
  const dragStartPosition = ref({ x: 0, y: 0 });
  const dragOffset = ref({ x: 0, y: 0 });

  // Reset state helper - defined early to avoid use-before-define
  const resetDragState = () => {
    draggedItem.value = null;
    dragOverColumn.value = null;
    isDragging.value = false;
    dragStartPosition.value = { x: 0, y: 0 };
    dragOffset.value = { x: 0, y: 0 };
  };

  // Methods
  const handleDragStart = (itemData, event = null) => {
    // Validate required data before storing
    if (!itemData || !itemData.conversationId || !itemData.fromColumn) {
      return;
    }

    // Store the drag data immediately for reliable drop handling
    draggedItem.value = {
      conversationId: itemData.conversationId,
      fromColumn: itemData.fromColumn,
    };
    isDragging.value = true;

    if (event) {
      // Capturar posição inicial para efeitos visuais
      dragStartPosition.value = {
        x: event.clientX,
        y: event.clientY,
      };

      // Configurar dados do drag - data is already set by KanbanCard
      event.dataTransfer.effectAllowed = 'move';

      // Note: KanbanCard already sets the dataTransfer data
      // We just need to track state here
    }
  };

  const handleDragOver = (event, columnName) => {
    if (!isDragging.value || !draggedItem.value) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    // Só atualizar se mudou de coluna
    if (dragOverColumn.value !== columnName) {
      dragOverColumn.value = columnName;
    }
  };

  const handleDragEnter = (event, columnName) => {
    if (!isDragging.value || !draggedItem.value) return;

    event.preventDefault();
    dragOverColumn.value = columnName;
  };

  const handleDragLeave = (event, columnName) => {
    if (!isDragging.value || !draggedItem.value) return;

    // Bug #17 Fix: Verificar se event.currentTarget existe antes de usar
    if (!event.currentTarget) return;

    // Só remover o highlight se realmente saiu da coluna
    // Verifica se o related target ainda está dentro da coluna
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (dragOverColumn.value === columnName) {
        dragOverColumn.value = null;
      }
    }
  };

  const handleDrop = (event, columnName) => {
    // Bug #17 Fix: Verificar se event existe
    if (!event) {
      resetDragState();
      return null;
    }

    event.preventDefault();

    try {
      // BUGFIX: Priority order for getting drag data:
      // 1. Try dataTransfer first (most reliable)
      // 2. Fall back to draggedItem state
      let itemData = null;

      // Bug #33 Fix: Try-catch específico para getData com fallback
      try {
        const transferData = event.dataTransfer?.getData('application/json');
        if (transferData) {
          const parsed = JSON.parse(transferData);
          if (parsed && parsed.conversationId) {
            itemData = parsed;
          }
        }
      } catch {
        // Fallback para draggedItem.value se parsing falhar
      }

      // Fall back to draggedItem state if dataTransfer didn't work
      if (!itemData && draggedItem.value && draggedItem.value.conversationId) {
        itemData = draggedItem.value;
      }

      // Validate all required fields before processing drop
      if (!itemData || !itemData.conversationId || !itemData.fromColumn) {
        resetDragState();
        return null;
      }

      // Don't move if dropped on same column
      if (itemData.fromColumn === columnName) {
        resetDragState();
        return null;
      }

      const result = {
        conversationId: itemData.conversationId,
        fromColumn: itemData.fromColumn,
        toColumn: columnName,
      };

      // Reset state
      resetDragState();

      return result;
    } catch {
      // Error in handleDrop - reset state
      resetDragState();
      return null;
    }
  };

  const handleDragEnd = () => {
    // Aguardar um pouco antes de resetar para permitir que o drop seja processado
    nextTick(() => {
      resetDragState();
    });
  };

  // Utility methods
  const canDropInColumn = (columnName, itemData = null) => {
    if (!itemData && !draggedItem.value) return false;

    const item = itemData || draggedItem.value;

    // Não pode mover para a mesma coluna
    if (item.fromColumn === columnName) return false;

    // Outras regras de negócio podem ser adicionadas aqui
    // Por exemplo: verificar permissões, status da conversa, etc.

    return true;
  };

  const getDropPosition = (event, columnElement) => {
    if (!columnElement) return 'bottom';

    const cards = [...columnElement.querySelectorAll('.kanban-card')];
    const mouseY = event.clientY;

    for (let i = 0; i < cards.length; i += 1) {
      const card = cards[i];
      const rect = card.getBoundingClientRect();
      const cardMiddle = rect.top + rect.height / 2;

      if (mouseY < cardMiddle) {
        return { position: 'before', index: i };
      }
    }

    return { position: 'after', index: cards.length };
  };

  // Touch support for mobile
  let touchStartData = null;

  const handleTouchStart = (itemData, event) => {
    touchStartData = {
      itemData,
      startX: event.touches[0].clientX,
      startY: event.touches[0].clientY,
      timestamp: Date.now(),
    };
  };

  const handleTouchMove = event => {
    if (!touchStartData) return;

    // Bug #24 Fix: Adicionar preventDefault para prevenir scroll em mobile
    if (isDragging.value) {
      event.preventDefault();
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartData.startX);
    const deltaY = Math.abs(touch.clientY - touchStartData.startY);

    // Só iniciar drag se moveu uma distância mínima
    if (deltaX > 10 || deltaY > 10) {
      if (!isDragging.value) {
        handleDragStart(touchStartData.itemData);
        // Bug #24 Fix: Prevenir scroll após iniciar drag
        event.preventDefault();
      }

      // Encontrar elemento sob o toque
      const elementBelow = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      const column = elementBelow?.closest('.kanban-column');

      if (column) {
        const columnName = column.dataset.columnName;
        if (columnName && dragOverColumn.value !== columnName) {
          dragOverColumn.value = columnName;
        }
      }
    }
  };

  const handleTouchEnd = event => {
    if (!touchStartData || !isDragging.value) {
      touchStartData = null;
      return;
    }

    const touch = event.changedTouches[0];
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );
    const column = elementBelow?.closest('.kanban-column');

    if (column) {
      const columnName = column.dataset.columnName;
      if (columnName) {
        const result = {
          conversationId: touchStartData.itemData.conversationId,
          fromColumn: touchStartData.itemData.fromColumn,
          toColumn: columnName,
        };

        resetDragState();
        touchStartData = null;

        return result;
      }
    }

    resetDragState();
    touchStartData = null;
    // No matching column found - return null (no action)
  };

  return {
    // State
    draggedItem,
    dragOverColumn,
    isDragging,
    dragStartPosition,
    dragOffset,

    // Methods
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resetDragState,

    // Utility
    canDropInColumn,
    getDropPosition,

    // Touch support
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
